// src/views/EmailCampaignsView.js
import React, { useState, useMemo, useEffect } from 'react';
import { db, functions, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { v4 as uuidv4 } from 'uuid';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, X, Plus, Send, Paperclip, Users, CheckSquare, Square, Filter } from 'lucide-react';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { Link as LinkIcon, Image as ImageIcon, Highlighter, Underline as UnderlineIcon } from 'lucide-react';
import CampaignDetailsModal from '../components/crm/CampaignDetailsModal';

// --- MENU BAR (Responsive) ---
const MenuBar = ({ editor, handleImageUpload }) => {
    if (!editor) return null;

    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl);
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };

    const buttonClass = (type, attrs = {}) => {
        return editor.isActive(type, attrs)
            ? 'is-active p-1.5 sm:p-2 bg-gray-200 rounded text-gray-800'
            : 'p-1.5 sm:p-2 hover:bg-gray-100 rounded text-gray-600';
    };

    return (
        <div className="flex flex-wrap items-center gap-1 sm:gap-2 p-2 border border-b-0 rounded-t-md bg-gray-50">
            <button onClick={() => editor.chain().focus().toggleBold().run()} className={buttonClass('bold')}><Bold size={16} /></button>
            <button onClick={() => editor.chain().focus().toggleItalic().run()} className={buttonClass('italic')}><Italic size={16} /></button>
            <button onClick={() => editor.chain().focus().toggleUnderline().run()} className={buttonClass('underline')}><UnderlineIcon size={16} /></button>
            <button onClick={() => editor.chain().focus().toggleHighlight({ color: '#ffc078' }).run()} className={buttonClass('highlight', { color: '#ffc078' })}><Highlighter size={16} /></button>
            <div className="w-px h-6 bg-gray-300 mx-1 hidden sm:block"></div>
            <button onClick={() => editor.chain().focus().toggleBulletList().run()} className={buttonClass('bulletList')}><List size={16} /></button>
            <button onClick={() => editor.chain().focus().toggleOrderedList().run()} className={buttonClass('orderedList')}><ListOrdered size={16} /></button>
            <div className="w-px h-6 bg-gray-300 mx-1 hidden sm:block"></div>
            <button onClick={setLink} className={buttonClass('link')}><LinkIcon size={16} /></button>
            <label htmlFor="image-upload" className="p-1.5 sm:p-2 hover:bg-gray-100 rounded cursor-pointer text-gray-600">
                <ImageIcon size={16} />
            </label>
            <input
                id="image-upload"
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0])}
            />
        </div>
    );
};

const EmailCampaignsView = ({ currentUserProfile }) => {
    const [filters, setFilters] = useState([]);
    const [matchMode, setMatchMode] = useState('all'); // ALL is the safe default: narrow audience unless the user opts into ANY
    const [subject, setSubject] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [attachment, setAttachment] = useState(null);
    const [audienceCount, setAudienceCount] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [campaignHistory, setCampaignHistory] = useState([]);
    const [viewingCampaign, setViewingCampaign] = useState(null);
    const [allTags, setAllTags] = useState([]); 
    const [allCustomers, setAllCustomers] = useState([]); 

    const [recipientList, setRecipientList] = useState([]); 
    const [excludedIds, setExcludedIds] = useState([]); 
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

    const cityOptions = useMemo(() => {
        if (!allCustomers) return [];
        const cities = allCustomers.map(c => c.city).filter(Boolean);
        const uniqueCities = [...new Set(cities)];
        return uniqueCities.sort().map(city => ({ id: city, label: city }));
    }, [allCustomers]);

    useEffect(() => {
        const debounce = setTimeout(() => {
            if (filters.length > 0) {
                setIsCalculating(true);
                const getPreview = httpsCallable(functions, 'getAudiencePreview');
                getPreview({ filters, matchMode })
                    .then(result => {
                        const list = result.data.recipients || [];
                        setRecipientList(list);
                        setAudienceCount(list.length);
                        setExcludedIds([]); 
                    })
                    .catch(err => console.error("Error calculating audience:", err))
                    .finally(() => setIsCalculating(false));
            } else {
                setAudienceCount(null);
                setRecipientList([]);
                setExcludedIds([]);
            }
        }, 500);

        return () => clearTimeout(debounce);
    }, [filters, matchMode]); // matchMode here => Total Recipients recomputes the instant ALL<->ANY is toggled

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;

        const historyQuery = query(
            collection(db, `organizations/${currentUserProfile.organizationId}/campaigns`),
            orderBy('sentAt', 'desc')
        );

        const unsubscribe = onSnapshot(historyQuery, (snapshot) => {
            setCampaignHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => unsubscribe();
    }, [currentUserProfile?.organizationId]);

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        const unsubscribeCustomers = onSnapshot(query(collection(db, `organizations/${orgId}/customers`)), (snapshot) => {
            setAllCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const unsubscribeTags = onSnapshot(query(collection(db, `organizations/${orgId}/tags`), orderBy('label')), (snapshot) => {
            setAllTags(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => { unsubscribeCustomers(); unsubscribeTags(); };
    }, [currentUserProfile?.organizationId]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ link: false, underline: false }),
            Underline, 
            Link.configure({ openOnClick: false }),
            Image,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
        ],
        content: '',
        editorProps: {
            attributes: {
                class: 'prose prose-sm sm:prose-base prose-ul:list-disc prose-ol:list-decimal max-w-none p-4 min-h-[250px] border rounded-b-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            },
        },
    });

    const addFilter = () => setFilters([...filters, { field: 'tag', operator: 'is', value: '' }]);
    const removeFilter = (index) => setFilters(filters.filter((_, i) => i !== index));
    
    const handleFilterChange = (index, part, value) => {
        const newFilters = [...filters];
        newFilters[index][part] = value;
        if (part === 'field') {
            newFilters[index].value = value === 'overdue' ? true : ''; 
        }
        setFilters(newFilters);
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) setAttachment(e.target.files[0]);
    };

    const toggleRecipient = (id) => {
        setExcludedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const finalRecipientCount = recipientList.length - excludedIds.length;

    const handleSendCampaign = async () => {
        const body = editor.getHTML();
        if (!subject.trim() || body === '<p></p>' || filters.length === 0) {
            alert("Please define at least one filter and provide a subject and body.");
            return;
        }
        
        if (finalRecipientCount === 0) {
            alert("You have excluded everyone. There is nobody to send to.");
            return;
        }

        if (!window.confirm(`Are you sure you want to send this campaign to ${finalRecipientCount} recipients?`)) {
            return;
        }

        setIsSending(true);
        try {
            let attachmentUrl = null;
            let attachmentFilename = null;

            if (attachment) {
                const filePath = `campaign-attachments/${currentUserProfile.organizationId}/${uuidv4()}-${attachment.name}`;
                const storageRef = ref(storage, filePath);
                await uploadBytes(storageRef, attachment);
                attachmentUrl = await getDownloadURL(storageRef);
                attachmentFilename = attachment.name;
            }

            const sendCampaignFn = httpsCallable(functions, 'sendBulkEmailCampaign');
            const result = await sendCampaignFn({
                filters,
                matchMode,
                subject,
                body,
                attachmentUrl,
                attachmentFilename,
                excludedIds
            });
            alert(result.data.message);
            setFilters([]);
            setSubject('');
            editor.commands.clearContent();
            setAttachment(null);
            setExcludedIds([]);
            setRecipientList([]);
        } catch (error) {
            console.error("Error sending campaign:", error);
            alert(`Failed to send campaign: ${error.message}`);
        } finally {
            setIsSending(false);
        }
    };

    const handleImageUpload = async (file) => {
        if (!file) return;
        try {
            const filePath = `campaign-images/${currentUserProfile.organizationId}/${uuidv4()}-${file.name}`;
            const storageRef = ref(storage, filePath);
            await uploadBytes(storageRef, file);
            const imageUrl = await getDownloadURL(storageRef);
            editor.chain().focus().setImage({ src: imageUrl }).run();
        } catch (error) {
            console.error("Error uploading image:", error);
            alert("Failed to upload image.");
        }
    };

    const filterOptions = [
        { value: 'all', label: 'All Clients' },
        { value: 'overdue', label: 'Has Overdue Balance' },
        { value: 'tag', label: 'Tag' },
        { value: 'status', label: 'Customer Status' },
        { value: 'city', label: 'City' },
        { value: 'lifetimeValue', label: 'Lifetime Value' },
        { value: 'createdAt', label: 'Date Added' },
        { value: 'lastContactDate', label: 'Not Contacted Since' },
        { value: 'lastPurchaseDate', label: 'Haven\'t Purchased Since' },
    ];

    const getValueOptions = (field) => {
        switch (field) {
            case 'tag': return allTags;
            case 'status': return [{ id: 'New', label: 'New' }, { id: 'Contacted', label: 'Contacted' }, { id: 'Proposal Sent', label: 'Proposal Sent' }, { id: 'Closed', label: 'Closed' }, { id: 'Lost', label: 'Lost' }];
            case 'city': return cityOptions;
            default: return null;
        }
    };

    return (
        <div className="p-4 sm:p-6 md:p-10 max-w-7xl mx-auto">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Send className="text-blue-600" size={28} /> Email Marketing
            </h1>
            
            <div className="flex flex-col lg:flex-row gap-6">
                
                {/* --- LEFT COLUMN: EDITOR --- */}
                <div className="flex-1 lg:w-2/3 space-y-6 order-2 lg:order-1">
                    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
                        <h2 className="text-lg font-bold mb-4 text-gray-800">Compose Email</h2>
                        <div className="mb-4">
                            <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-1">Subject Line</label>
                            <input 
                                type="text" 
                                id="subject" 
                                value={subject} 
                                onChange={(e) => setSubject(e.target.value)} 
                                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" 
                                placeholder="Enter a catchy subject..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Message Body</label>
                            <div className="border border-gray-300 rounded-lg shadow-sm">
                                <MenuBar editor={editor} handleImageUpload={handleImageUpload} />
                                <EditorContent editor={editor} />
                            </div>
                        </div>
                        <div className="mt-4">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Attachments</label>
                            <div className="flex flex-wrap items-center gap-3">
                                <label htmlFor="file-upload" className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors">
                                    <Paperclip size={16} />
                                    <span>Attach File</span>
                                </label>
                                <input id="file-upload" name="file-upload" type="file" className="sr-only" onChange={handleFileChange} />
                                {attachment && (
                                    <div className="flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm">
                                        <span className="truncate max-w-[150px]">{attachment.name}</span>
                                        <button onClick={() => setAttachment(null)} className="ml-2 hover:text-red-600"><X size={14} /></button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- RIGHT COLUMN: AUDIENCE BUILDER --- */}
                <div className="lg:w-1/3 space-y-6 order-1 lg:order-2">
                    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 sticky top-4">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <Filter size={20} className="text-gray-500"/> Target Audience
                            </h2>
                        </div>
                        
                        <div className="flex rounded-lg border border-gray-300 overflow-hidden mb-3 text-sm font-semibold">
                            <button type="button" onClick={() => setMatchMode('all')}
                                className={`flex-1 py-2 ${matchMode === 'all' ? 'bg-[#5F4EC9] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                Match ALL
                            </button>
                            <button type="button" onClick={() => setMatchMode('any')}
                                className={`flex-1 py-2 border-l border-gray-300 ${matchMode === 'any' ? 'bg-[#5F4EC9] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                                Match ANY
                            </button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4 bg-blue-50 p-3 rounded-lg">
                            {matchMode === 'all'
                                ? <>Recipients must match <strong>ALL</strong> conditions below.</>
                                : <>Recipients match <strong>ANY ONE</strong> of the conditions below.</>}
                        </p>

                        <div className="space-y-3 mb-4">
                            {filters.map((filter, index) => {
                                const valueOptions = getValueOptions(filter.field);
                                const requiresOperator = ['lifetimeValue', 'createdAt', 'lastContactDate', 'lastPurchaseDate'].includes(filter.field);
                                const requiresDaysInput = ['createdAt', 'lastContactDate', 'lastPurchaseDate'].includes(filter.field);
                                
                                return (
                                    <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200 relative group">
                                        <button onClick={() => removeFilter(index)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><X size={16} /></button>
                                        
                                        <div className="flex flex-col gap-2">
                                            <select value={filter.field} onChange={(e) => handleFilterChange(index, 'field', e.target.value)} className="p-2 border border-gray-300 rounded-md text-sm w-full font-medium">
                                                {filterOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>

                                            {filter.field !== 'all' && filter.field !== 'overdue' && (
                                                <div className="flex gap-2">
                                                    {requiresOperator && (
                                                        <select value={filter.operator} onChange={(e) => handleFilterChange(index, 'operator', e.target.value)} className="p-2 border border-gray-300 rounded-md text-sm w-1/3">
                                                            <option value=">">&gt;</option>
                                                            <option value="<">&lt;</option>
                                                            {filter.field === 'lifetimeValue' && <option value="==">=</option>}
                                                        </select>
                                                    )}
                                                    
                                                    {valueOptions ? (
                                                        <select value={filter.value} onChange={(e) => handleFilterChange(index, 'value', e.target.value)} className="p-2 border border-gray-300 rounded-md text-sm flex-grow">
                                                            <option value="">-- Select --</option>
                                                            {valueOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                                                        </select>
                                                    ) : (
                                                        <div className="flex items-center flex-grow bg-white border border-gray-300 rounded-md px-2">
                                                            {filter.field === 'lifetimeValue' && <span className="text-gray-500">$</span>}
                                                            <input
                                                                type="number"
                                                                placeholder="Value"
                                                                value={filter.value}
                                                                onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                                                                className="p-2 text-sm w-full outline-none"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {requiresDaysInput && <span className="text-xs text-gray-500 text-right">days ago</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <button onClick={addFilter} className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-600 font-medium transition-colors flex items-center justify-center gap-2 mb-6">
                            <Plus size={16} /> Add Condition
                        </button>
                        
                        <div className="bg-gray-900 rounded-xl p-6 text-center text-white mb-6">
                            {isCalculating ? (
                                <p className="text-sm opacity-70 animate-pulse">Calculating audience...</p>
                            ) : (
                                <>
                                    <p className="text-sm opacity-70 mb-1">Total Recipients</p>
                                    <p className="text-4xl font-bold mb-3">{audienceCount !== null ? finalRecipientCount : '-'}</p>
                                    
                                    {audienceCount !== null && (
                                        <button 
                                            onClick={() => setIsReviewModalOpen(true)}
                                            className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors flex items-center justify-center gap-1 mx-auto"
                                        >
                                            <Users size={12} /> Review List
                                        </button>
                                    )}
                                </>
                            )}
                        </div>

                        <button 
                            onClick={handleSendCampaign} 
                            disabled={isSending || isCalculating || (audienceCount !== null && finalRecipientCount === 0)} 
                            className="w-full flex justify-center items-center gap-2 px-4 py-3.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                        >
                            <Send size={18} />
                            {isSending ? 'Sending Campaign...' : 'Send Now'}
                        </button>
                    </div>
                </div>
            </div>

            {/* --- RECIPIENT REVIEW MODAL --- */}
            {isReviewModalOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                            <h3 className="font-bold text-gray-800">Review Recipients</h3>
                            <button onClick={() => setIsReviewModalOpen(false)} className="p-1 hover:bg-gray-200 rounded-full"><X size={20}/></button>
                        </div>
                        
                        <div className="p-2 overflow-y-auto flex-grow space-y-1 bg-gray-50/50">
                            {recipientList.length === 0 ? (
                                <div className="p-8 text-center text-gray-500">No recipients matched your filters.</div>
                            ) : (
                                recipientList.map(user => {
                                    const isExcluded = excludedIds.includes(user.id);
                                    return (
                                        <div 
                                            key={user.id} 
                                            onClick={() => toggleRecipient(user.id)}
                                            className={`flex items-center p-3 mx-2 rounded-lg cursor-pointer border transition-all ${isExcluded ? 'bg-gray-100 border-transparent opacity-60' : 'bg-white border-gray-200 shadow-sm hover:border-blue-300'}`}
                                        >
                                            <div className={`mr-3 ${isExcluded ? 'text-gray-400' : 'text-blue-600'}`}>
                                                {isExcluded ? <Square size={20} /> : <CheckSquare size={20} />}
                                            </div>
                                            <div>
                                                <p className={`font-medium text-sm ${isExcluded ? 'text-gray-500 line-through' : 'text-gray-900'}`}>{user.name}</p>
                                                <p className="text-xs text-gray-500">{user.email}</p>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="p-4 border-t bg-white flex justify-between items-center">
                            <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                                {finalRecipientCount} recipients selected
                            </span>
                            <button 
                                onClick={() => setIsReviewModalOpen(false)}
                                className="px-5 py-2 bg-gray-900 text-white rounded-lg hover:bg-black text-sm font-bold shadow-md"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CAMPAIGN HISTORY --- */}
            <div className="mt-10 bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold mb-4 text-gray-800">Campaign History</h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                            <tr>
                                <th className="px-4 sm:px-6 py-3 whitespace-nowrap">Date Sent</th>
                                <th className="px-4 sm:px-6 py-3">Subject</th>
                                <th className="px-4 sm:px-6 py-3 text-center">Recipients</th>
                                <th className="px-4 sm:px-6 py-3 text-center">Opens</th>
                                <th className="px-4 sm:px-6 py-3 text-center">Clicks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaignHistory.length > 0 ? (
                                campaignHistory.map(campaign => (
                                    <tr key={campaign.id} className="bg-white border-b hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => setViewingCampaign(campaign)}>
                                        <td className="px-4 sm:px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                                            {campaign.sentAt?.toDate().toLocaleDateString()}
                                        </td>
                                        <td className="px-4 sm:px-6 py-4 max-w-[200px] truncate">{campaign.subject}</td>
                                        <td className="px-4 sm:px-6 py-4 text-center font-medium">{campaign.recipientCount || 0}</td>
                                        <td className="px-4 sm:px-6 py-4 text-center font-medium text-green-600">{campaign.openCount || 0}</td>
                                        <td className="px-4 sm:px-6 py-4 text-center font-medium text-blue-600">{campaign.clickCount || 0}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="text-center py-10 text-gray-500 italic">No campaigns sent yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {viewingCampaign && <CampaignDetailsModal campaign={viewingCampaign} onClose={() => setViewingCampaign(null)} />}
        </div>
    );
};

export default EmailCampaignsView;